import api from '../../utils/api';

export interface VendorBankDetails {
  bankDetailsId: string;
  vendorId: string;
  accountHolderName: string;
  accountNumber: string;
  ifscCode: string;
  bankName: string;
  branchName?: string | null;
  accountType: 'SAVINGS' | 'CURRENT' | 'OTHER' | string;
  mobileNumber?: string | null;
  isVerified: boolean;
  verifiedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type VendorBankDetailsInput = {
  accountHolderName: string;
  accountNumber: string;
  ifscCode: string;
  bankName: string;
  branchName?: string;
  accountType: 'SAVINGS' | 'CURRENT' | 'OTHER';
  mobileNumber?: string;
};

type ApiEnvelope<T> = {
  success: boolean;
  data: T;
  message?: string;
};

/** Master-admin JWT — no vendor bank password required */
export const fetchVendorBankDetails = async (vendorId: string): Promise<VendorBankDetails | null> => {
  try {
    const response = await api.get<ApiEnvelope<VendorBankDetails>>(
      `/master-admin/vendors/${encodeURIComponent(vendorId)}/bank-details`
    );
    return response.data.data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
};

export const createVendorBankDetails = async (
  vendorId: string,
  body: VendorBankDetailsInput
): Promise<VendorBankDetails> => {
  const response = await api.post<ApiEnvelope<VendorBankDetails>>(
    `/master-admin/vendors/${encodeURIComponent(vendorId)}/bank-details`,
    body
  );
  return response.data.data;
};

export const updateVendorBankDetails = async (
  vendorId: string,
  body: Partial<VendorBankDetailsInput>
): Promise<VendorBankDetails> => {
  const response = await api.put<ApiEnvelope<VendorBankDetails>>(
    `/master-admin/vendors/${encodeURIComponent(vendorId)}/bank-details`,
    body
  );
  return response.data.data;
};

/** Mark bank details verified (or unverified if verify is false). */
export const verifyVendorBankDetails = async (
  vendorId: string,
  verify: boolean
): Promise<VendorBankDetails> => {
  const response = await api.patch<ApiEnvelope<VendorBankDetails>>(
    `/master-admin/vendors/${encodeURIComponent(vendorId)}/bank-details/verify`,
    { verify }
  );
  return response.data.data;
};

/** Matches backend `ifscVerification.controller` — Razorpay IFSC lookup via backend proxy */
export type IfscVerificationData = {
  ifscCode: string;
  branchName: string | null;
  bankName: string | null;
};

export const verifyIfscCode = async (ifscCode: string): Promise<IfscVerificationData> => {
  const code = String(ifscCode || '').trim().toUpperCase();
  const response = await api.get<{ success: boolean; data: IfscVerificationData; message?: string }>(
    `/banking/verify-ifsc/${encodeURIComponent(code)}`
  );
  if (!response.data?.data) {
    const msg = response.data?.message || 'IFSC verification failed';
    throw { response: { data: { message: msg } } };
  }
  return response.data.data;
};
